import type React from 'react';

import { type PropsWithChildren } from 'react';
import { useEffect, useMemo, useState } from 'react';

import { type DictionaryEntryDto } from '@/api/generated/models/dictionary-entry-dto';
import { type DictionaryEnumDto } from '@/api/generated/models/dictionary-enum-dto';
import { DictionaryEnumTypeDto } from '@/api/generated/models/dictionary-enum-type-dto';
import { DictionaryLanguageDto } from '@/api/generated/models/dictionary-language-dto';
import { DictionaryTypeDto } from '@/api/generated/models/dictionary-type-dto';
import { type VacancyProjectStatusDto } from '@/api/generated/models/vacancy-project-status-dto';
import { useGetEnums } from '@/api/util/requests/useGetEnums.util.ts';
import { useDictionaryTranslation } from '@/common/useDictionaryTranslation.ts';
import { type SelectSearchOptionModel } from '@/components/form/model/SelectSearchOption.model.ts';
import FullPageSpinner from '@/components/ui/fullPageSpinner/FullPageSpinner';
import { useUserContext } from '@/contexts/userContext/User.context.ts';
import { useGetAllDictionaries } from '@/views/dictionaries/components/dictionariesTable/util/useGetAllDictionaries.util.ts';

import { DictionariesContext, type DictionariesContextProps } from './Dictionaries.context';

const emptyDictionaries: DictionariesContext = {
  assessmentCompanySizesOptions: [],
  certificateCategoriesOptions: [],
  certificatesOptions: [],
  currenciesOptions: [],
  industryOptions: [],
  languagesOptions: [],
  locationsOptions: [],
  offeringsCategoriesOptions: [],
  offeringsOptions: [],
  skillCategoriesOptions: [],
  skillsOptions: [],
};

type DictionaryTypeMappingModel = Partial<
  Record<
    DictionaryTypeDto,
    {
      options: keyof DictionariesContext;
    }
  >
>;

export const DictionaryTypeMapping: DictionaryTypeMappingModel = {
  [DictionaryTypeDto.AssessmentCompanySize]: {
    options: 'assessmentCompanySizesOptions',
  },
  [DictionaryTypeDto.Certificate]: { options: 'certificatesOptions' },
  [DictionaryTypeDto.CertificateCategory]: { options: 'certificateCategoriesOptions' },
  [DictionaryTypeDto.Currency]: { options: 'currenciesOptions' },
  [DictionaryTypeDto.Industry]: { options: 'industryOptions' },
  [DictionaryTypeDto.Language]: { options: 'languagesOptions' },
  [DictionaryTypeDto.Location]: { options: 'locationsOptions' },
  [DictionaryTypeDto.Offering]: { options: 'offeringsOptions' },
  [DictionaryTypeDto.OfferingCategory]: { options: 'offeringsCategoriesOptions' },
  [DictionaryTypeDto.Skill]: { options: 'skillsOptions' },
  [DictionaryTypeDto.SkillCategory]: { options: 'skillCategoriesOptions' },
};

export const DictionariesContextProvider: React.FC<PropsWithChildren> = props => {
  const { dt } = useDictionaryTranslation();
  const [flags, setFlags] = useState(0);
  const [dictionaryEnums, setDictionaryEnums] = useState<DictionaryEnumDto[]>([]);
  const [groupedDictionaries, setGroupedDictionaries] = useState<DictionariesContext>({
    ...emptyDictionaries,
  });
  const [vacancyProjectStatuses, setVacancyProjectStatuses] = useState<
    SelectSearchOptionModel<VacancyProjectStatusDto>[]
  >([]);
  const {
    user: { dataReady, initializationDone },
  } = useUserContext();

  const parseDictionaries = (dicts: DictionaryEntryDto[]) => {
    const newDict = structuredClone(emptyDictionaries);
    dicts.forEach(dict => {
      const labels = {};

      dict.locales.forEach(it => {
        (labels as Record<DictionaryLanguageDto, string>)[it.locale] = it.label;
      });
      const dictionaryMapping = DictionaryTypeMapping[dict.type];
      if (!dictionaryMapping) {
        return;
      }
      const option: SelectSearchOptionModel<number> = {
        ...dict,
        label: labels,
        value: dict.id,
      };
      newDict[dictionaryMapping.options].push(option);
    });
    setGroupedDictionaries(newDict);
    setFlags(cur => cur | (1 << 1));
  };

  const { dictionaries, isFetchingDictionariesTypes } = useGetAllDictionaries();
  const { getEnums } = useGetEnums();

  // Fetch enums independently; dictionaries are fetched automatically by the hook above
  const fetchDictionaryEnums = async () => {
    const enums = await getEnums();
    setDictionaryEnums(enums);
    const statuses = enums.find(
      enumList => enumList.type === DictionaryEnumTypeDto.ProjectStatus,
    )?.values;
    setVacancyProjectStatuses(
      statuses?.map(status => ({
        label: { [DictionaryLanguageDto.En]: dt('projectStatus', status) },
        value: status as VacancyProjectStatusDto,
      })) ?? [],
    );
    setFlags(cur => cur | Math.trunc(1));
  };

  useEffect(() => {
    if (!dataReady) {
      return;
    }
    void fetchDictionaryEnums();
  }, [dataReady]);

  // When dictionaries arrive from the auto query, parse and store them in context
  useEffect(() => {
    if (!dataReady) {
      return;
    }
    if (dictionaries && dictionaries.length > 0) {
      parseDictionaries(dictionaries);
    }
  }, [dataReady, dictionaries]);

  const context: DictionariesContextProps = useMemo(() => {
    return {
      ...groupedDictionaries,
      dictionaryEnums,
      setGroupedDictionaries,
      vacancyProjectStatuses,
    };
  }, [dictionaryEnums, groupedDictionaries, vacancyProjectStatuses]);

  if (!dataReady || (flags < 3 && initializationDone) || isFetchingDictionariesTypes) {
    return <FullPageSpinner />;
  }

  return <DictionariesContext value={context}>{props.children}</DictionariesContext>;
};
